import Artcloud from '../../artcloud.js';

import {
    Activity,
    ActivityStatus,
    BillingPermission,
    CollateralExport,
    DateFormat,
    ExportStatus,
    Owner,
    Subscription,
    Trial,
    User
} from './Type.jsx';
import { knownTypesParams } from '../results/notificationTypes';

function observableRepository(url) {
    return {
        _callbacks: {},
        _data: null,
        _pending: false,
        _tempCallbacks: [],
        _url: url,

        addObserverCallback: function (name, callback) {
            this._callbacks[name] = callback;
        },

        addTempObserverCallback: function (callback) {
            this._tempCallbacks.push(callback);
        },

        clear: function () {
            this._data = null;
            this._pending = false;
        },

        current: function () {
            return this._data;
        },

        fetch: function (onError, q) {
            if (!this || this._pending) {
                return;
            }
            this._data = null;
            this._pending = true;

            const component = this;
            Artcloud.data.get(
                this._url,
                q,
                function (d) {
                    component._pending = false;
                    component._data = d;

                    component.notify(d);
                }, onError);
        },

        insert: function (d) {
            if (!this._data) {
                this._data = { Data: [] };
            }
            this._data.Data.push(d);
        },

        notify: function (data) {
            let callback;
            for (let name in this._callbacks) {
                callback = this._callbacks[name];
                if (!callback) {
                    continue;
                }
                callback(data);
            }
            this._tempCallbacks.map(function (callback) {
                if (callback) {
                    callback(data);
                }
            });
            this._tempCallbacks = [];
        },

        // Drop the slot rather than nulling it, so per-mount keys don't pile up dead entries for notify() to walk.
        removeObserverCallback: function (name) {
            delete this._callbacks[name];
        },

        replace: function (data, sendNotifications) {
            this._data = data;
            if (sendNotifications != false) {
                this.notify(this._data);
            }
        }
    }
}

const Batch = observableRepository(Artcloud.router.batches.get());
const ExchangeRates = observableRepository(Artcloud.router.settings.currencyRates.get());
const Import = observableRepository(Artcloud.router.imports.get());
const InventoryTypes = observableRepository(Artcloud.router.inventoryTypes.get());
const InvoiceStages = observableRepository(Artcloud.router.invoiceStages.get());
const Locations = observableRepository(Artcloud.router.locations.get());
const OpportunityStages = observableRepository(Artcloud.router.opportunities.stages.get());
const Signature = observableRepository(Artcloud.router.threads.signatures.me.get());
const Staff = observableRepository(Artcloud.router.staff.get());

const Export = observableRepository(Artcloud.router.exports.get());
Export.Pending = {
    _data: {},

    add: (exportId, type) => Export.Pending._data[exportId] = type,
    clear: () => Export.Pending._data = {},
    get: (exportId) => Export.Pending._data[exportId],
    remove: (exportId) => Export.Pending._data[exportId] = undefined
};
Export.fetchGlobal = (onError) => { Export.fetch(onError, { owner: Owner.Mine, status: ExportStatus.Pending.value }) };

const MailingLists = observableRepository(Artcloud.router.mailingLists.get());
MailingLists.mergeInto = function (data) {
    const _data = data || {};
    const mailingLists = MailingLists.current();
    if (!mailingLists) {
        return _data;
    }
    for (let id in mailingLists) {
        if (!_data[id]) {
            _data[id] = [];
        }
        _data[id] = _data[id].concat(mailingLists[id]);
    }
    return _data;
}

const Messages = observableRepository(Artcloud.router.messages.get());
Messages.fetchGlobal = (onError) => { Messages.fetch(onError, { bound: 0, limit: 3, owner: Owner.Mine, status: ActivityStatus.Unread }) };

const Notifications = observableRepository(Artcloud.router.notifications.get());
Notifications.fetchGlobal = (onError) => { Notifications.fetch(onError, { limit: 3, status: ActivityStatus.Unread, type: Activity.Global.value, ...knownTypesParams() }) };

const Session = observableRepository(Artcloud.router.sessions.get());
Session.getManagerSubscriptionTier = (session) => Session._hasManagerSubscriptionTier(session)
    ? session.Subscriptions.Manager.Tier.Value
    : -1;

Session._hasManagerSubscription = (session) => session && session.Subscriptions && session.Subscriptions.Manager;

Session._hasManagerSubscriptionTier = (session) => Session._hasManagerSubscription(session) && session.Subscriptions.Manager.Tier;

Session.canAddSales = (session) => session && session.Permissions && session.Permissions.Staff && session.Permissions.Staff.CanAddSales;

Session.getManagerSubscriptionTierName = (session) => Session._hasManagerSubscriptionTier(session)
    ? session.Subscriptions.Manager.Tier.Name
    : '';

Session.hasFeature = (session, feature) => Boolean(session?.Permissions?.Features?.[feature]);

const NOTIFICATION_CENTER = 'NotificationCenter';
Session.isNotificationCenter = (session) => Session.hasFeature(session, NOTIFICATION_CENTER);

Session.hasSalesAddOn = (session) => session && session.Subscriptions && session.Subscriptions.AddOns && session.Subscriptions.AddOns.HasSales;

Session.isArtist = (session) => session && session.Type && session.Type.Value == User.Artist.value;

Session.isArtsyIntegrated = (session) => session && session.Permissions && session.Permissions.IsArtsyIntegrated;

Session.isBeta = (session) => session && session.Permissions && session.Permissions.IsBeta;

Session.isBillingAnnual = (session) => session && session.Subscriptions && session.Subscriptions.BillingPeriod == 'Annual' ? 1 : 0;

Session.isLegacyPricing = (session) => {
    switch (Subscription.Tier.getType(Session.getManagerSubscriptionTier(session))) {
        case Subscription.Tier.Basic:
        case Subscription.Tier.Pro:
        case Subscription.Tier.ProPlus:
            return true;
    }
    switch (Subscription.Tier.getType(Session.getWebsiteSubscriptionTier(session))) {
        case Subscription.Tier.Basic:
        case Subscription.Tier.Pro:
        case Subscription.Tier.ProPlus:
            return !Session.isArtist(session);
    }
    return false;
};

Session.isManagerExpertOrTrialing = (session) => Session.getManagerSubscriptionTier(session) === Subscription.Tier.getValue(Subscription.Tier.Expert_202508) || Session.getTrialState(session) === Trial.Trialing;

Session.isManagerFree = (session) => !Session._hasManagerSubscriptionTier(session) || session.Subscriptions.Manager.Tier.Value == Subscription.Tier.getValue(Subscription.Tier.Free);

Session.isManagerLegacy = (session) => Session._hasManagerSubscription(session) && session.Subscriptions.Manager.Legacy && session.Subscriptions.Manager.Legacy.Value == 1;

Session.isManagerProPlusOrTrialing = (session) => {
    const tier = Session.getManagerSubscriptionTier(session);
    return tier === Subscription.Tier.getValue(Subscription.Tier.ProPlus) || tier === Subscription.Tier.getValue(Subscription.Tier.ProPlus_202602) || Session.getTrialState(session) === Trial.Trialing;
}

Session.isTrial = (session) => { const trialState = Session.getTrialState(session); return trialState == Trial.Trialing || trialState == Trial.Expired; }

Session.getTrialState = (session) => {
    let trialState = Trial.None;
    if (session) {
        if (Session.getManagerSubscriptionTier(session) > 0) {
            trialState = session.Subscriptions.Manager.TrialState;
        } else if (Session.getWebsiteSubscriptionTier(session) > 0) {
            trialState = session.Subscriptions.Website.TrialState;
        }
    }
    return trialState;
};

Session.getWebsiteSubscriptionTier = (session) => Session._hasWebsiteSubscriptionTier(session)
    ? session.Subscriptions.Website.Tier.Value
    : -1;

Session._hasWebsiteSubscription = (session) => session && session.Subscriptions && session.Subscriptions.Website;

Session._hasWebsiteSubscriptionTier = (session) => Session._hasWebsiteSubscription(session) && session.Subscriptions.Website.Tier;

Session.isWebsiteExpertOrTrialing = (session) => Session.getWebsiteSubscriptionTier(session) === Subscription.Tier.getValue(Subscription.Tier.Expert_202508) || Session.getTrialState(session) === Trial.Trialing;

Session.isWebsiteFree = (session) => !Session._hasWebsiteSubscriptionTier(session) || session.Subscriptions.Website.Tier.Value == Subscription.Tier.getValue(Subscription.Tier.Free);

Session.isWebsiteLegacy = (session) => Session._hasWebsiteSubscription(session) && session.Subscriptions.Website.Legacy && session.Subscriptions.Website.Legacy.Value == 1;

Session.isWebsiteProPlusOrTrialing = (session) => Session.getWebsiteSubscriptionTier(session) === Subscription.Tier.getValue(Subscription.Tier.ProPlus) || Session.getTrialState(session) === Trial.Trialing;

Session.appendWebsiteTierToRoute = (session, route) => {
    let _route = route;
    if (session && session.Subscriptions.Website && session.Subscriptions.Website.Tier) {
        _route += `?websiteTier=${session.Subscriptions.Website.Tier.Value}`;
    }
    return _route;
}
Session.canOnlySeeOwnedClients = (session) => session && session.Permissions.Staff && session.Permissions.Staff.CanOnlySeeOwnedClients;
Session.canOnlySeeOwnedInvoices = (session) => session && session.Permissions.Staff && session.Permissions.Staff.CanOnlyViewAssociatedInvoices;
Session.canOnlySeeOwnedOpportunities = (session) => session && session.Permissions.Staff && session.Permissions.Staff.CanOnlyViewAssociatedOpportunities;
Session.hasStaff = (session) => session && session.Permissions && session.Permissions.HasStaff;

Session.dateFormat = (session) => {
    let dateFormat = DateFormat.defaultFormatString;
    if (session && session.Config) {
        dateFormat = DateFormat.formatStringForType(session.Config.DateFormat.Value);
    }
    return dateFormat;
}

Session.firstDayOfWeek = (session) => DateFormat.firstDayOfWeekForType(session?.Config?.DateFormat?.Value);

const Usage = observableRepository(Artcloud.router.billing.usage.get());
Usage.refresh = (session, onError, { callback, product, type } = {}) => {
    const data = {};
    if (session.Subscriptions) {
        if (session.Subscriptions.Manager) {
            if (session.Subscriptions.Manager.Legacy) {
                data.legacy = session.Subscriptions.Manager.Legacy.Value
            }
            if (session.Subscriptions.Manager.Tier) {
                data.tier = session.Subscriptions.Manager.Tier.Value
            }
        }
        if (session.Subscriptions.Campaign && session.Subscriptions.Campaign.Tier) {
            data.campaignTier = session.Subscriptions.Campaign.Tier.Value;
        }
        if (session.Subscriptions.Website && session.Subscriptions.Website.Tier) {
            data.websiteTier = session.Subscriptions.Website.Tier.Value;
        }
    }
    if (type == undefined || !Usage.current()) {
        Usage.fetch(onError, data);
        return;
    }
    Artcloud.data.get(
        Artcloud.router.billing.usage.get(type),
        data,
        (responseData) => {
            let key;
            switch (type) {
                case BillingPermission.Action.CreateClient:
                    key = 'Contacts';
                    break;
                case BillingPermission.Action.SendCampaign:
                    key = 'Emails';
                    break;
                case BillingPermission.Action.CreateInventory:
                    key = 'Inventory';
                    break;
                case BillingPermission.Action.CreateInvoice:
                    key = 'Invoices';
                    break;
                case BillingPermission.Action.OptArtistWebsite:
                    key = 'Artists';
                    break;
                case BillingPermission.Action.OptInventoryWebsite:
                    key = 'Inventory';
                    break;
                default:
                    usage.Warning = responseData.Warning;
                    break;
            }
            const usage = Usage.current();
            if (key && usage[product]) {
                usage[product][key] = responseData;
            }
            switch (product) {
                case Subscription.Product.Website:
                    usage.IsWebsiteSoftGated = false;
                    for (let temp in usage[product]) {
                        if (usage[product][temp].IsWebsiteSoftGated) {
                            usage.IsWebsiteSoftGated = true;
                            break;
                        }
                    }
                    break;
                default:
                    usage.IsSoftGated = false;
                    for (let temp in usage[product]) {
                        if (usage[product][temp].IsSoftGated) {
                            usage.IsSoftGated = true;
                            break;
                        }
                    }
                    break;
            }
            Usage.replace(usage, true);

            if (callback) {
                callback(usage);
            }
        },
        onError
    );
}

const Collateral = {
    Configs: {
        normalizeType: (type) => {
            let _type = type;
            switch (_type) {
                case CollateralExport.CertificateOfAuthenticityList:
                    _type = CollateralExport.CertificateOfAuthenticity;
                    break;
                case CollateralExport.MosaicItems:
                    _type = CollateralExport.Mosaic;
                    break;
                case CollateralExport.PricelistItems:
                    _type = CollateralExport.Pricelist;
                    break;
                case CollateralExport.Arrivals:
                case CollateralExport.TearsheetList:
                    _type = CollateralExport.Tearsheet;
                    break;
                case CollateralExport.WallTagItems:
                    _type = CollateralExport.WallTag;
                    break;
            }
            return _type;
        }
    },

    ExportValue: {
        [CollateralExport.PurchaseOrder]: 0,
        [CollateralExport.Pricelist]: 1,
        [CollateralExport.Tearsheet]: 2,
        [CollateralExport.Invoice]: 3,
        [CollateralExport.Offer]: 4,
        [CollateralExport.CertificateOfAuthenticity]: 5,
        [CollateralExport.ArtistBio]: 6,
        [CollateralExport.Consignment]: 7,
        [CollateralExport.Mosaic]: 8
    },

    configTypeFromExportType: (exportType) => {
        const normalizedType = Collateral.Configs.normalizeType(exportType);
        return Collateral.ExportValue[normalizedType];
    },

    exportTypeFromConfigType: (configType) => {
        switch (configType) {
            case Collateral.ExportValue[CollateralExport.PurchaseOrder]:
                return CollateralExport.PurchaseOrder;
            case Collateral.ExportValue[CollateralExport.Pricelist]:
                return CollateralExport.Pricelist;
            case Collateral.ExportValue[CollateralExport.Tearsheet]:
                return CollateralExport.Tearsheet;
            case Collateral.ExportValue[CollateralExport.Invoice]:
                return CollateralExport.Invoice;
            case Collateral.ExportValue[CollateralExport.Offer]:
                return CollateralExport.Offer;
            case Collateral.ExportValue[CollateralExport.CertificateOfAuthenticity]:
                return CollateralExport.CertificateOfAuthenticity;
            case Collateral.ExportValue[CollateralExport.ArtistBio]:
                return CollateralExport.ArtistBio;
            case Collateral.ExportValue[CollateralExport.Consignment]:
                return CollateralExport.Consignment;
            case Collateral.ExportValue[CollateralExport.Mosaic]:
                return CollateralExport.Mosaic;
        }
    },

    getUrlData: (type, ids) => {
        let urlData;
        if (!ids || !ids.length) {
            return urlData;
        }
        let _id = ids[0];
        let key;
        switch (type) {
            case CollateralExport.ArtistBio:
                key = 'ArtistIds';
                _id = ids;
                break;
            case CollateralExport.Consignment:
                key = 'ConsignmentId';
                break;
            case CollateralExport.Invoice:
            case CollateralExport.Offer:
            case CollateralExport.PurchaseOrder:
                key = 'InvoiceId';
                break;
            case CollateralExport.CertificateOfAuthenticityList:
            case CollateralExport.Mosaic:
            case CollateralExport.Pricelist:
            case CollateralExport.TearsheetList:
                key = 'QuicklistId';
                break;
            case CollateralExport.CertificateOfAuthenticity:
            case CollateralExport.MosaicItems:
            case CollateralExport.PricelistItems:
            case CollateralExport.Tearsheet:
                key = 'EditionIds';
                _id = ids;
                break;
        }
        if (key) {
            urlData = { [key]: _id };
        }
        return urlData;
    }
};

const Data = {
    _dirty: {},

    Types: {
        Account: 'admin/accounts',
        Artist: 'artists',
        Audience: 'audiences',
        Blog: 'website/blogs',
        BrandPackage: 'admin/brandPackages',
        Campaign: 'campaigns',
        CampaignDomains: 'settings/campaigns/domains',
        CampaignTemplate: 'settings/campaigns',
        City: 'admin/cities',
        Client: 'clients',
        Collateral: 'settings/collateralStyle',
        Composition: 'website/compositions',
        Consignment: 'consignments',
        Domain: 'website/domains',
        EditionSets: 'editionSets',
        EditionTitles: 'editionTitles',
        EmailTemplate: 'settings/emailTemplates',
        GallerySales: 'gallerySales',
        Inventory: 'inventory',
        Invoice: 'invoices',
        Media: 'website/media',
        Message: 'messages',
        NewArrivals: 'newArrivals',
        Notification: 'notifications',
        Opportunity: 'opportunities',
        Pages: 'website/pages',
        PromoCode: 'settings/promoCodes',
        PrivateView: 'privateViews',
        PurchaseOrder: 'purchaseOrders',
        Quicklist: 'quicklists',
        Redirect: 'website/redirects',
        ReleaseNote: 'admin/releaseNotes',
        Representative: 'galleries',
        Show: 'shows',
        Todo: 'todos',
        WebsiteComposition: 'website/compositions',
        WebsiteThemes: 'website/themes'
    },

    isDirty: function (name) {
        return this._dirty[name] != null;
    },

    setClean: function (name) {
        this._dirty[name] = null;
    },

    setDirty: function (name) {
        this._dirty[name] = true;
    }
}

const Font = {
    legacyTypes: [
        { value: 1, label: 'Times New Roman' },
        { value: 2, label: 'Arial' },
        { value: 6, label: 'Open Sans' },
        { value: 7, label: 'Abril Fatface' },
        { value: 8, label: 'Exo 2' },
        { value: 9, label: 'Montserrat' },
        { value: 10, label: 'News Cycle' },
        { value: 11, label: 'Pinyon Script' },
        { value: 12, label: 'Lato' },
        { value: 13, label: 'Archivo' },
        { value: 14, label: 'Titillium Web' },
        { value: 15, label: 'Futura' },
        { value: 16, label: 'Raleway Light' },
        { value: 17, label: 'Cormorant Garamond' },
        { value: 18, label: 'Gyre Adventor' },
        { value: 19, label: 'Avenir' },
        { value: 20, label: 'Libre Franklin' },
        { value: 21, label: 'Josefin Sans' },
        { value: 22, label: 'Minion Pro' },
        { value: 23, label: 'Avenir Book' },
        { value: 24, label: 'Avenir Medium' },
        { value: 25, label: 'Roboto' },
        { value: 26, label: 'Homepage-Baukasten Book' },
        { value: 27, label: 'Regencie Light' },
        { value: 28, label: 'Century Gothic' },
        { value: 29, label: 'Recoleta' },
        { value: 30, label: 'TT Jenevers' },
        { value: 31, label: 'Borna' },
        { value: 32, label: 'Mr Eaves Sans' },
        { value: 33, label: 'TT Barrels' },
    ],

    createFontList: (bodyFont, headerFont, availableFonts) => {
        let _bodyFont = bodyFont;
        let _headerFont = headerFont;
        const fonts = [];
        if (availableFonts) {
            availableFonts.forEach((d) => {
                const type = d.type || d.Type;
                switch (type) {
                    case 'Body':
                        _bodyFont = d;
                        break;
                    case 'Header':
                        _headerFont = d;
                        break;
                    default:
                        const family = d.family || d.Family;
                        fonts.push({ label: family, family, value: `'${family}'`, variants: d.variants || d.Variants })
                        break;
                }
            })
        }
        const additional = [];
        if (_headerFont) {
            const family = _headerFont.family || _headerFont.Family;
            additional.push({ label: family, family, value: `'${family}'`, variants: _headerFont.variants || _headerFont.Variants });
        }
        if (_bodyFont) {
            const family = _bodyFont.family || _bodyFont.Family;
            if (additional.length > 0 && additional[0].family != family) {
                additional.push({ label: family, family, value: `'${family}'`, variants: _bodyFont.variants || _bodyFont.Variants });
            }
        }
        if (additional.length > 0) {
            if (fonts.length > 0) {
                fonts.splice(0, 0, { type: 'separator' })
            }
            fonts.splice(0, 0, ...additional)
        }
        return fonts;
    },

    generateGoogleFontLink: (fontFamilies) => `https://fonts.googleapis.com/css?family=${fontFamilies}`,

    getLegacyFonts: (styleguide) => {
        let bodyFont, headerFont;
        for (let i = 0; i < Font.legacyTypes.length; i++) {
            const temp = Font.legacyTypes[i];
            if (temp.value == styleguide.HeaderFont) {
                headerFont = { family: temp.label };
            } else if (temp.value == styleguide.BodyFont) {
                bodyFont = { family: temp.label };
            }
            if (bodyFont && headerFont) {
                break;
            }
        }
        return { bodyFont, headerFont };
    }
}

const PageKit = {
    _callbacks: {},

    addObserverCallback: (name, callback) => {
        PageKit._callbacks[name] = callback;
    },
    removeObserverCallback: function (name) {
        delete PageKit._callbacks[name];
    },
    update: (data) => {
        let callback;
        for (let name in PageKit._callbacks) {
            callback = PageKit._callbacks[name];
            if (!callback) {
                continue;
            }
            callback(data);
        }
    }
}

const Tab = {
    // Sweeps the pre-2026-08 per-tab destination stacks; return targets now live in navigation/returnTarget.ts.
    cleanUp: function () {
        if (typeof Storage === 'undefined') {
            return;
        }
        const compareTime = (new Date().getTime()) - 43200000; // 12 hours
        const prefix = 'destination_';
        for (let key in localStorage) {
            if (!key || !key.startsWith(prefix)) {
                continue;
            }
            const time = key.replace(prefix, '');
            if (!time) {
                continue;
            }
            const date = new Date();
            date.setTime(time);
            if (!date) {
                return;
            }
            if (date.getTime() < compareTime) {
                localStorage.removeItem(key);
            }
        }
    }
}

export {
    Batch,
    Collateral,
    Data,
    ExchangeRates,
    Export,
    Font,
    Import,
    InventoryTypes,
    InvoiceStages,
    Locations,
    MailingLists,
    Messages,
    Notifications,
    OpportunityStages,
    PageKit,
    Session,
    Signature,
    Staff,
    Tab,
    Usage
}